import type { MaintenanceCadenceConfig, MaintenanceChecklist, Quote, ServiceReport } from '../../types';
import { parseLocalDate, toLocalYYYYMMDD } from '../../utils/scheduleDates';

export type MaintenanceStatus = 'SCHEDULED' | 'DUE' | 'OVERDUE' | 'COMPLETED' | 'SKIPPED' | 'PAUSED';

export interface MaintenanceDueItem {
  quoteId: string;
  projectName: string;
  clientName: string;
  checklistId: string;
  checklistName: string;
  frequency: MaintenanceChecklist['frequency'];
  lastCompletedDate?: string;
  nextDueDate: string;
  status: MaintenanceStatus;
}

export const STATUS_RANK: Record<MaintenanceStatus, number> = {
  OVERDUE: 0,
  DUE: 1,
  SCHEDULED: 2,
  COMPLETED: 3,
  SKIPPED: 4,
  PAUSED: 5,
};

interface ComputeDueListOpts {
  todayYYYYMMDD?: string;
  limit?: number;
}

function parseSafeDate(dateStr: string | undefined): Date | null {
  if (!dateStr) return null;
  const date = parseLocalDate(dateStr);
  return Number.isNaN(date.getTime()) ? null : date;
}

function daysBetween(dateStr1: string, dateStr2: string): number {
  const date1 = parseLocalDate(dateStr1);
  const date2 = parseLocalDate(dateStr2);
  const diffMs = date2.getTime() - date1.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

export function getLastCompletedReport(reports: ServiceReport[] = [], checklistId: string): ServiceReport | null {
  const matches = reports.filter((r) => r.checklistResults.some((item) => item.checklistId === checklistId));
  if (matches.length === 0) return null;

  const completedReports = matches.filter((r) => typeof r.completed === 'boolean' ? r.completed === true : true);
  const candidate = completedReports.length > 0 ? completedReports : matches;
  candidate.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return candidate[0] || null;
}

export function getMaintenanceStatus(params: {
  checklist: MaintenanceChecklist;
  nextDueDate: string;
  lastCompletedReport: ServiceReport | null;
  cadenceConfig?: MaintenanceCadenceConfig;
  todayYYYYMMDD?: string;
}): MaintenanceStatus {
  const today = params.todayYYYYMMDD || toLocalYYYYMMDD();
  const cadence = params.cadenceConfig;

  if (cadence?.mode === 'ANCHOR') {
    const currentPeriod = today.substring(0, 7);
    if (params.lastCompletedReport?.servicePeriod === currentPeriod) {
      return 'COMPLETED';
    }
  }

  const daysUntilDue = daysBetween(today, params.nextDueDate);
  if (daysUntilDue < 0) return 'OVERDUE';
  if (daysUntilDue <= 7) return 'DUE';
  return 'SCHEDULED';
}

export function computeDueList(quotes: Quote[], opts: ComputeDueListOpts = {}): MaintenanceDueItem[] {
  const today = opts.todayYYYYMMDD || toLocalYYYYMMDD();
  const results: MaintenanceDueItem[] = [];
  const limit = opts.limit ?? 20;

  for (const quote of quotes) {
    if (quote.status !== 'WON' || !quote.maintenanceChecklists?.length) continue;

    for (const checklist of quote.maintenanceChecklists) {
      const lastReport = getLastCompletedReport(quote.serviceReports || [], checklist.id);
      const cadence = quote.maintenanceSettings?.cadenceConfig?.[checklist.id];
      const nextDueDate = quote.maintenanceNextDue?.[checklist.id]?.nextDueDate || toLocalYYYYMMDD();
      const status = getMaintenanceStatus({
        checklist,
        nextDueDate,
        lastCompletedReport: lastReport,
        cadenceConfig: cadence,
        todayYYYYMMDD: today,
      });

      results.push({
        quoteId: quote.id,
        projectName: quote.projectName,
        clientName: quote.clientName,
        checklistId: checklist.id,
        checklistName: checklist.name,
        frequency: checklist.frequency,
        lastCompletedDate: lastReport?.visitDate || lastReport?.servicePeriod || lastReport?.date,
        nextDueDate,
        status,
      });
    }
  }

  results.sort((a, b) => {
    const rankDiff = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    if (rankDiff !== 0) return rankDiff;
    if (a.nextDueDate !== b.nextDueDate) return a.nextDueDate.localeCompare(b.nextDueDate);
    if (a.quoteId !== b.quoteId) return a.quoteId.localeCompare(b.quoteId);
    return a.checklistId.localeCompare(b.checklistId);
  });

  return results.slice(0, limit);
}
